import { useRef, useEffect } from 'react'
import { useClickOutside } from '../../../hooks/useClickOutside'

function parseDateToInput(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return ''
  const [day, month, year] = dateStr.split('.')
  if (!day || !month || !year) return ''
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

function parseDateFromInput(inputStr) {
  if (!inputStr) return ''
  const [year, month, day] = inputStr.split('-')
  return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`
}

export function AnimeWatchlistEdit({
  item,
  editData,
  setEditData,
  handleSaveItemChanges,
  getUpdatedWatchItem,
  handleCloseEdit,
  handleOpenReviewPopup,
  userAlreadyReviewed,
  onRemove,
}) {
  const editModalRef = useRef()
  useClickOutside(editModalRef, handleCloseEdit, true)

  useEffect(() => {
    if (item.start_date !== undefined && editData.start_date === undefined) {
      setEditData((prev) => ({ ...prev, start_date: item.start_date || '' }))
    }
    if (item.finish_date !== undefined && editData.finish_date === undefined) {
      setEditData((prev) => ({ ...prev, finish_date: item.finish_date || '' }))
    }
  }, [item, editData.start_date, editData.finish_date, setEditData])

  const statusOptions = [
    { value: 'Watching', label: 'כרגע צופה', color: 'bg-primary' },
    { value: 'Plan to Watch', label: 'מתכנן לצפות', color: 'bg-primary' },
    { value: 'Completed', label: 'סיימתי לצפות', color: 'bg-primary' },
    { value: 'On-Hold', label: 'בהמתנה', color: 'bg-primary' },
    { value: 'Dropped', label: 'ננטש', color: 'bg-primary' },
  ]

  const parsedEpisodes = parseInt(item.episodes)
  const isEpisodesValid = !isNaN(parsedEpisodes) && parsedEpisodes > 0
  const maxEpisodes = isEpisodesValid ? parsedEpisodes : 2000

  const startDateInput = parseDateToInput(editData.start_date)
  const finishDateInput = parseDateToInput(editData.finish_date)

  const handleSubmit = () => {
    const updatedItem = getUpdatedWatchItem({ ...item, ...editData })
    handleSaveItemChanges(updatedItem)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div
        ref={editModalRef}
        className="relative bg-secondaryBackground text-text p-7 rounded-3xl w-full max-w-xl shadow-2xl border border-borderM animate-fadeIn"
        dir="rtl"
      >
        <button
          onClick={handleCloseEdit}
          className="absolute left-4 top-4 bg-gray-300 hover:bg-gray-400 rounded-full w-9 h-9 flex items-center justify-center text-gray-700 transition"
          aria-label="סגור"
        >
          ×
        </button>

        <h3 className="text-2xl font-bold mb-7 text-center tracking-tight">
          עדכון רשימת צפייה
        </h3>

        <div className="mb-6">
          <label className="block mb-2 text-softText font-medium">סטטוס:</label>
          <div className="flex gap-2 flex-wrap">
            {statusOptions.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`px-4 py-2 rounded-full text-white text-sm font-semibold transition border-2 ${
                  editData.status === s.value
                    ? `${s.color}`
                    : 'bg-background border-borderM'
                }`}
                onClick={() => setEditData({ ...editData, status: s.value })}
                aria-pressed={editData.status === s.value}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-softText font-medium">
            התקדמות:
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max={maxEpisodes}
              value={editData.progress}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  progress: Math.max(
                    0,
                    Math.min(parseInt(e.target.value || '0', 10), maxEpisodes)
                  ),
                })
              }
              className="w-24 p-2 bg-background border border-borderM rounded outline-none text-center font-bold"
            />
            <span className="text-softText text-sm">
              {isEpisodesValid ? `/ ${parsedEpisodes}` : '/ ?'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-7">
          <div>
            <label className="block mb-2 text-softText font-medium">
              תאריך התחלה:
            </label>
            <input
              type="date"
              value={startDateInput}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  start_date: parseDateFromInput(e.target.value),
                })
              }
              className="w-full p-2 bg-background border border-borderM rounded outline-none"
            />
          </div>
          <div>
            <label className="block mb-2 text-softText font-medium">
              תאריך סיום:
            </label>
            <input
              type="date"
              value={finishDateInput}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  finish_date: parseDateFromInput(e.target.value),
                })
              }
              className="w-full p-2 bg-background border border-borderM rounded outline-none"
            />
          </div>
        </div>

        <button
          onClick={() => handleOpenReviewPopup(item.anizone_id)}
          className="w-full flex items-center justify-center gap-2 bg-accentLight hover:bg-accent text-white py-3 rounded-xl mb-6 text-lg font-semibold transition"
        >
          <span className="material-icons">⭐</span>
          {userAlreadyReviewed(item.anizone_id) ? 'ערוך דירוג' : 'תן דירוג'}
        </button>

        <div className="flex gap-3 mb-2">
          <button
            onClick={() => onRemove(item.anizone_id)}
            className="flex-1 bg-primary hover:bg-red-700 text-white py-2 rounded-lg font-semibold transition"
          >
            הסר מרשימת הצפייה
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold transition"
          >
            שמור שינויים
          </button>
        </div>
      </div>
    </div>
  )
}
